import { useEffect, useMemo, useRef, useState } from 'react'

/**
 * A dropdown with a search box, for a list too long to scroll — transport
 * services, of which there will be many.
 *
 * `options` is [{ value, label }]; `value` is the chosen one's value. The
 * search matches anywhere in the label, ignoring case. Arrow keys move, Enter
 * chooses, Escape and a click outside close it.
 */
export default function SearchSelect({ value, options, onChange, ariaLabel, placeholder = 'Search…' }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef(null)
  const searchRef = useRef(null)

  const chosen = options.find((o) => String(o.value) === String(value)) ?? options[0]
  const shown = useMemo(() => {
    const term = query.trim().toLowerCase()
    return term ? options.filter((o) => o.label.toLowerCase().includes(term)) : options
  }, [options, query])

  useEffect(() => {
    if (!open) return undefined
    setQuery('')
    setActive(Math.max(0, options.findIndex((o) => String(o.value) === String(value))))
    searchRef.current?.focus()
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (option) => {
    setOpen(false)
    if (option && String(option.value) !== String(value)) onChange(option.value)
  }

  return (
    <div className="search-select" ref={rootRef}>
      <button
        type="button"
        className="search-select-button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="search-select-value">{chosen?.label}</span>
        <span className="search-select-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="search-select-panel">
          <input
            ref={searchRef}
            type="search"
            className="search-select-search"
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActive(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((i) => Math.min(i + 1, shown.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((i) => Math.max(i - 1, 0))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                pick(shown[active])
              } else if (e.key === 'Escape') {
                setOpen(false)
              }
            }}
          />
          <ul className="search-select-list" role="listbox" aria-label={ariaLabel}>
            {shown.map((option, i) => (
              <li
                key={String(option.value)}
                role="option"
                aria-selected={String(option.value) === String(value)}
                className={`${i === active ? 'active' : ''}${
                  String(option.value) === String(value) ? ' chosen' : ''
                }`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(option)
                }}
              >
                {option.label}
              </li>
            ))}
            {shown.length === 0 && <li className="search-select-empty">No match</li>}
          </ul>
        </div>
      )}
    </div>
  )
}
